import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Brain,
  Send,
  Loader2,
  MessageSquare,
  Sparkles,
  Lightbulb,
  History,
  Trash2,
  Plus,
  Cpu,
  Database,
  BarChart3,
  BookOpen,
  Tag,
  Star,
  Zap,
  Eye,
  Trash,
  Search,
  Filter,
  RefreshCw
} from 'lucide-react';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';

const SecurIA = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [message, setMessage] = useState('');
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [showSidebar, setShowSidebar] = useState(true);
  
  // 🧠 Memory State
  const [memories, setMemories] = useState([]);
  const [memoryStats, setMemoryStats] = useState(null);
  const [showMemoryPanel, setShowMemoryPanel] = useState(false);
  const [memoryFilter, setMemoryFilter] = useState('all');
  const [memorySearch, setMemorySearch] = useState('');
  
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchSessions = useCallback(async () => {
    try {
      const response = await api.get('/securia/sessions');
      setSessions(response.data || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  }, []);

  const fetchMemories = useCallback(async () => {
    try {
      const [memoriesRes, statsRes] = await Promise.all([
        api.get('/securia/memories?limit=100'),
        api.get('/securia/memories/stats')
      ]);
      setMemories(memoriesRes.data || []);
      setMemoryStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching memories:', error);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
    fetchMemories();
  }, [fetchSessions, fetchMemories]);

  const loadSession = async (sessionId) => {
    try {
      const response = await api.get(`/securia/sessions/${sessionId}`);
      setCurrentSession(response.data);
      setMessages(response.data.messages || []);
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de charger la session', variant: 'destructive' });
    }
  };

  const startNewSession = () => {
    setCurrentSession(null);
    setMessages([]);
    setSuggestions([]);
  };

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/securia/sessions/${sessionId}`);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (currentSession?.id === sessionId) {
        startNewSession();
      }
      toast({ title: 'Session supprimée' });
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const sendMessage = async (text = message) => {
    if (!text.trim()) return;

    const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setMessage('');
    setLoading(true);

    try {
      const response = await api.post('/securia/chat', {
        message: text,
        sessionId: currentSession?.id,
        includeSystemData: true,
        includeMemory: true
      });

      const assistantMsg = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString(),
        metadata: {
          memoriesUsed: response.data.memoriesUsed,
          newMemories: response.data.newMemories
        }
      };
      setMessages(prev => [...prev, assistantMsg]);
      setSuggestions(response.data.suggestions || []);

      if (!currentSession) {
        setCurrentSession({ id: response.data.sessionId });
        fetchSessions();
      }

      // Refresh memories if new ones were created
      if (response.data.newMemories > 0) {
        fetchMemories();
        toast({ 
          title: '🧠 Nouvelle mémoire', 
          description: `${response.data.newMemories} souvenir(s) enregistré(s)` 
        });
      }
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de communiquer avec SecurIA', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await api.post('/securia/analyze');
      setAnalysis(response.data);
      setMemoryStats(response.data.memoryStats);
      toast({ title: '✅ Analyse terminée', description: `Score de santé: ${response.data.healthScore}%` });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Analyse impossible', variant: 'destructive' });
    } finally {
      setAnalyzing(false);
    }
  };

  const deleteMemory = async (memoryId) => {
    try {
      await api.delete(`/securia/memories/${memoryId}`);
      setMemories(prev => prev.filter(m => m.id !== memoryId));
      toast({ title: 'Mémoire supprimée' });
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const reinforceMemory = async (memoryId) => {
    try {
      await api.post(`/securia/memories/${memoryId}/reinforce?boost=2`);
      fetchMemories();
      toast({ title: '⭐ Mémoire renforcée' });
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const filteredMemories = memories.filter(m => {
    if (memoryFilter !== 'all' && m.type !== memoryFilter) return false;
    if (memorySearch && !m.content.toLowerCase().includes(memorySearch.toLowerCase())) return false;
    return true;
  });

  const getTypeIcon = (type) => {
    switch(type) {
      case 'fact': return '📋';
      case 'preference': return '⭐';
      case 'procedure': return '📝';
      case 'pattern': return '🔄';
      default: return '💭';
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'fact': return 'bg-blue-900/50 text-blue-400';
      case 'preference': return 'bg-yellow-900/50 text-yellow-400';
      case 'procedure': return 'bg-green-900/50 text-green-400';
      case 'pattern': return 'bg-purple-900/50 text-purple-400';
      default: return 'bg-slate-700 text-slate-300';
    }
  };

  const quickQuestions = [
    { icon: '📊', text: 'Analyse mes performances ce mois' },
    { icon: '👥', text: 'Comment optimiser mon planning agents ?' },
    { icon: '💰', text: 'Résume les variables de paie' },
    { icon: '🔐', text: 'Meilleures pratiques sécurité ?' },
    { icon: '📈', text: 'Comment moderniser mes opérations ?' },
    { icon: '🧠', text: 'Que sais-tu sur mon entreprise ?' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white">
      <div className="flex h-screen">
        {/* Sidebar */}
        {showSidebar && (
          <div className="w-72 bg-slate-900/80 border-r border-slate-700 flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-slate-700">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl animate-pulse">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                    SecurIA
                  </h1>
                  <p className="text-xs text-slate-400">🧠 Mémoire active</p>
                </div>
              </div>
              <Button onClick={startNewSession} className="w-full bg-purple-600 hover:bg-purple-700">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle conversation
              </Button>
            </div>

            {/* Memory Stats */}
            {memoryStats && (
              <div className="p-3 border-b border-slate-700 bg-purple-900/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-purple-300 font-medium flex items-center gap-1">
                    <Database className="w-3 h-3" /> Mémoire
                  </span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowMemoryPanel(!showMemoryPanel)}
                    className="text-purple-400 hover:text-purple-300 p-1"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div className="bg-slate-800/50 rounded p-2">
                    <p className="text-lg font-bold text-white">{memoryStats.totalMemories || 0}</p>
                    <p className="text-xs text-slate-400">Souvenirs</p>
                  </div>
                  <div className="bg-slate-800/50 rounded p-2">
                    <p className="text-lg font-bold text-white">{memoryStats.recentlyAccessed || 0}</p>
                    <p className="text-xs text-slate-400">Récents</p>
                  </div>
                </div>
              </div>
            )}

            {/* Sessions */}
            <div className="flex-1 overflow-y-auto p-2">
              <p className="text-xs text-slate-500 px-2 py-1">Conversations récentes</p>
              {sessions.map((session) => (
                <div
                  key={session.id}
                  onClick={() => loadSession(session.id)}
                  className={`flex items-center justify-between p-2 rounded-lg cursor-pointer group transition-colors
                    ${currentSession?.id === session.id ? 'bg-purple-900/50' : 'hover:bg-slate-800'}`}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <MessageSquare className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span className="text-sm truncate text-slate-300">{session.title}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => deleteSession(session.id, e)}
                    className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 p-1"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Quick Memory Preview */}
            <div className="border-t border-slate-700 p-4">
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-medium text-slate-300">Derniers souvenirs</span>
              </div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {memories.slice(0, 3).map((m, i) => (
                  <div key={i} className="text-xs text-slate-400 bg-slate-800/50 rounded p-2 flex items-start gap-2">
                    <span>{getTypeIcon(m.type)}</span>
                    <span className="flex-1 line-clamp-2">{m.content}</span>
                  </div>
                ))}
                {memories.length === 0 && (
                  <p className="text-xs text-slate-500">Aucun souvenir encore. Discutez pour créer des mémoires !</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Memory Panel */}
        {showMemoryPanel && (
          <div className="w-96 bg-slate-900/95 border-r border-slate-700 flex flex-col">
            <div className="p-4 border-b border-slate-700">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Database className="w-5 h-5 text-purple-400" />
                  Mémoire à Long Terme
                </h2>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowMemoryPanel(false)}
                  className="text-slate-400"
                >
                  ✕
                </Button>
              </div>
              
              {/* Search & Filter */}
              <div className="flex gap-2 mb-3">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    placeholder="Rechercher..."
                    value={memorySearch}
                    onChange={(e) => setMemorySearch(e.target.value)}
                    className="pl-8 bg-slate-800 border-slate-700 text-white text-sm"
                  />
                </div>
                <Button variant="outline" size="sm" onClick={fetchMemories} className="border-slate-700">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>

              {/* Type Filter */}
              <div className="flex gap-1 flex-wrap">
                {['all', 'fact', 'preference', 'procedure', 'pattern'].map(type => (
                  <Button
                    key={type}
                    variant={memoryFilter === type ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMemoryFilter(type)}
                    className={`text-xs ${memoryFilter === type ? 'bg-purple-600' : 'border-slate-700'}`}
                  >
                    {type === 'all' ? 'Tout' : getTypeIcon(type)}
                  </Button>
                ))}
              </div>
            </div>

            {/* Memory List */}
            <div className="flex-1 overflow-y-auto p-2 space-y-2">
              {filteredMemories.map((memory) => (
                <Card key={memory.id} className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs ${getTypeColor(memory.type)}`}>
                            {getTypeIcon(memory.type)} {memory.type}
                          </span>
                          <span className="text-xs text-slate-500">{memory.category}</span>
                        </div>
                        <p className="text-sm text-slate-300">{memory.content}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-yellow-500 flex items-center gap-1">
                            <Star className="w-3 h-3" /> {memory.importance}
                          </span>
                          <span className="text-xs text-slate-500">
                            {memory.accessCount}x accédé
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => reinforceMemory(memory.id)}
                          className="text-yellow-400 hover:text-yellow-300 p-1"
                        >
                          <Zap className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => deleteMemory(memory.id)}
                          className="text-red-400 hover:text-red-300 p-1"
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {filteredMemories.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <Database className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Aucune mémoire trouvée</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col">
          {/* Top Bar */}
          <div className="bg-slate-900/50 border-b border-slate-700 p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSidebar(!showSidebar)}
                className="text-slate-400"
              >
                <History className="w-5 h-5" />
              </Button>
              <div>
                <h2 className="text-lg font-semibold text-white">
                  {currentSession ? 'Conversation en cours' : 'Nouvelle conversation'}
                </h2>
                <p className="text-xs text-slate-400">
                  🧠 {memoryStats?.totalMemories || 0} souvenirs en mémoire
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowMemoryPanel(!showMemoryPanel)}
                className="border-slate-700 text-slate-300"
              >
                <Database className="w-4 h-4 mr-2" />
                Mémoire
              </Button>
              <Button
                onClick={runAnalysis}
                disabled={analyzing}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                {analyzing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <BarChart3 className="w-4 h-4 mr-2" />
                )}
                Analyser
              </Button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-6">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full">
                <div className="p-6 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-3xl mb-8 relative">
                  <Cpu className="w-16 h-16 text-purple-400" />
                  <div className="absolute -top-2 -right-2 bg-green-500 rounded-full p-1">
                    <Database className="w-4 h-4 text-white" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">SecurIA avec Mémoire</h2>
                <p className="text-slate-400 mb-2 text-center max-w-md">
                  Je me souviens de <strong className="text-purple-400">TOUT</strong> ce que vous me dites.
                </p>
                <p className="text-slate-500 mb-8 text-center max-w-md text-sm">
                  Plus vous discutez avec moi, plus je vous connais et mieux je vous aide.
                </p>

                {/* Quick Questions */}
                <div className="grid grid-cols-2 gap-3 max-w-2xl">
                  {quickQuestions.map((q, i) => (
                    <Button
                      key={i}
                      variant="outline"
                      onClick={() => sendMessage(q.text)}
                      className="justify-start text-left h-auto py-3 px-4 border-slate-700 bg-slate-800/50 hover:bg-slate-700 text-slate-300"
                    >
                      <span className="mr-2">{q.icon}</span>
                      <span className="text-sm">{q.text}</span>
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4 max-w-4xl mx-auto">
                {messages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        msg.role === 'user'
                          ? 'bg-purple-600 text-white'
                          : 'bg-slate-800 text-slate-200'
                      }`}
                    >
                      {msg.role === 'assistant' && (
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-purple-400" />
                          <span className="text-xs text-purple-400 font-medium">SecurIA</span>
                          {msg.metadata?.memoriesUsed > 0 && (
                            <span className="text-xs bg-purple-900/50 px-2 py-0.5 rounded text-purple-300">
                              🧠 {msg.metadata.memoriesUsed} souvenirs utilisés
                            </span>
                          )}
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      {msg.metadata?.newMemories > 0 && (
                        <div className="mt-2 text-xs text-green-400 flex items-center gap-1">
                          <Database className="w-3 h-3" />
                          +{msg.metadata.newMemories} nouveau(x) souvenir(s)
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-slate-800 rounded-2xl px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
                        <span className="text-sm text-slate-400">SecurIA réfléchit...</span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Suggestions */}
          {suggestions.length > 0 && (
            <div className="px-6 py-2 flex gap-2 flex-wrap">
              {suggestions.map((s, i) => (
                <Button
                  key={i}
                  variant="outline"
                  size="sm"
                  onClick={() => sendMessage(s)}
                  className="border-slate-700 bg-slate-800/50 hover:bg-slate-700 text-slate-300 text-xs"
                >
                  <Lightbulb className="w-3 h-3 mr-1 text-yellow-400" />
                  {s}
                </Button>
              ))}
            </div>
          )}

          {/* Input Area */}
          <div className="p-4 border-t border-slate-700 bg-slate-900/50">
            <div className="max-w-4xl mx-auto flex gap-3">
              <Input
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !loading && sendMessage()}
                placeholder="Posez votre question à SecurIA (je me souviendrai de tout)..."
                className="flex-1 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                disabled={loading}
              />
              <Button
                onClick={() => sendMessage()}
                disabled={loading || !message.trim()}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurIA;
